import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, AppLink, C, Callout, Step } from "./primitives.js";

export const meta = {
  id: "regenerate-article",
  title: "Refreshing & regenerating a published article",
  category: "Workflows",
  lastUpdated: "2026-07-11",
  description: "Re-run the pipeline on a live article that dropped in ranking or needs updating — without losing its original publish date.",
  relatedGuides: ["produce-article", "publish-sanity", "asset-storage"],
  readTimeMin: 5,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        When a published article drops in ranking or a topic gains new content potential, you don't start over — you <strong>regenerate</strong> it. The <C>Regenerate</C> button on the keyword's detail panel opens a guided, four-step wizard that re-runs the pipeline against a fresh SERP and re-publishes to the <em>same</em> Sanity document — critically, <strong>keeping the article's original publish date</strong> while advancing its "last updated" date.
      </Lead>

      <Callout kind="tip">
        <C>Regenerate</C> only appears once an article is live (status <C>published</C>/<C>distributed</C>, or it has a stored original publish date). For a brand-new article, use the normal Enrich → Brief → Produce → Publish flow in <AppLink to="help?guide=produce-article">From keyword to published article</AppLink>.
      </Callout>

      <H2>Why the publish date matters</H2>
      <P>
        Search engines read two dates from an article: <C>datePublished</C> (when it first went live) and <C>dateModified</C> (when it last changed). A genuine refresh should advance <C>dateModified</C> to signal freshness, but must <strong>not</strong> move <C>datePublished</C> — resetting it tells Google the page is brand-new and forfeits the authority it accrued over time.
      </P>
      <P>
        The wizard handles this for you. The first time an article goes live, its publish date is captured (and stored as <C>firstPublishedAt</C>). Every later re-publish reuses that anchor for <C>datePublished</C>, while <C>dateModified</C> follows the article's last-reviewed date. The <strong>Publish to Sanity</strong> button gets the same protection.
      </P>

      <H2>The four steps</H2>

      <Step n={1} title="Re-check the SERP">
        <P>
          Diffs the live SERP against the stored snapshot (AI-Overview flip, new/removed People-Also-Ask, top-10 competitor churn) and stamps a fresh review date. It returns a recommendation — <C>no-op</C>, <C>review-prose</C>, or <C>regenerate-article</C> — so you know how much has actually moved. This step alone is what advances <C>dateModified</C>.
        </P>
      </Step>

      <Step n={2} title="Regenerate content (optional)">
        <P>
          Two independent actions, each optional:
        </P>
        <Ul>
          <Li><strong>Regenerate H1 + meta</strong> — re-derives the SERP-grounded H1, meta title, and meta description (see the meta section of <AppLink to="help?guide=produce-article">From keyword to published article</AppLink>). Past variations are kept so you can compare and pick.</Li>
          <Li><strong>Regenerate body</strong> — re-writes the full article from the current brief. Costs tokens (like a normal Produce) and shows live section progress. Skip it if the SERP barely moved.</Li>
        </Ul>
      </Step>

      <Step n={3} title="Images — keep, regenerate, or replace specific ones">
        <P>
          Default is <strong>Keep all</strong> (zero cost — the existing images are reused as-is at publish). Switch to <strong>Replace some</strong> to see a grid of every image (hero, each in-content section, social, thumbnail) and either <strong>regenerate all</strong> or tick just the ones you want redrawn. Only the images you pick are regenerated; the rest are untouched.
        </P>
      </Step>

      <Step n={4} title="Re-publish (with the original date preserved)">
        <P>
          Hit <strong>Preview (dry-run)</strong> first — it shows the exact <C>publishedAt</C> that will be written (your original date, preserved) plus how many images resolved, without touching Sanity. When it looks right, <strong>Publish live</strong> pushes the regenerated article to the same document.
        </P>
      </Step>

      <Callout kind="tip">
        Re-publishing an already-live article updates only the machine-generated <strong>content</strong> (title, body, meta, JSON-LD, table of contents, reading time). Your <strong>Sanity Studio edits are preserved</strong> — categories, tags, the main image, the author, and any custom fields are only filled in when empty, never overwritten, and the original publish date is kept. (It re-publishes with a <C>patch</C> instead of a full replace.)
      </Callout>
      <Callout kind="warn">
        One nuance from that: because the main image is preserved, <strong>regenerating images won't replace a hero that's already set on the live doc</strong>. If you regenerated images and want the new hero live, set/clear it in Sanity Studio.
      </Callout>

      <H2>Keep the slug stable</H2>
      <P>
        The wizard re-publishes to the same Sanity document id, which is derived from the slug. If you change the slug during a meta regen, you create a <em>new</em> page (and orphan the old URL — it needs a redirect). Only change the slug deliberately.
      </P>
    </>
  );
}

export const guide: Guide = { meta, Body };
